import { Collapse, TableRow } from '@mui/material';
import { ThreatTypes } from '../../helpers/types';
import useThreatExpandData from './useThreatExpandData';
import {
  ExpandedRowContainer,
  ExpandedCell,
  ExpandedSecondRow,
} from './ThreatExpandedRow.style';
import {
  useExpandedDerivedData,
  ExpandedScreenshot,
  ExpandedSummary,
  ExpandedDomainInfo,
  ExpandedDnsRecords,
  ExpandedStatusHistory,
  ExpandedExternalTools,
  ExpandedTakedownDetails,
} from '../ExpandedRow';

interface ThreatExpandedRowProps {
  threat: ThreatTypes;
  isExpanded: boolean;
  threatNetworkData: any;
}

const ThreatExpandedRow = ({
  threat,
  isExpanded,
  threatNetworkData,
}: ThreatExpandedRowProps) => {
  const {
    threatDetail,
    gsbLiveInfo,
    dnsRecords,
    statusHistory,
    takedownItems,
    takedownStatuses,
    isLoading,
    gsbLoading,
    dnsLoading,
    statusHistoryLoading,
    takedownLoading,
  } = useThreatExpandData(threat, isExpanded);

  const whoisData = threatNetworkData?.whois;
  const sslData = threatNetworkData?.ssl;

  const score = threatDetail?.score?.value ?? threat.score?.value ?? 0;
  const status =
    threatDetail?.identification_status?.value ??
    threat.identification_status?.value;

  const {
    preCrimeLevel,
    gsbThreatTypes,
    dnsARecord,
    filteredDnsRecords,
    sortedStatusHistory,
    findStatusNameById,
  } = useExpandedDerivedData({
    score,
    gsbLiveInfo,
    dnsRecords,
    statusHistory,
    takedownStatuses,
  });

  const detailUrl = `/threats/${threat.protected_asset_id}/${threat.threat_id}/${threat.company_id}`;

  return (
    <TableRow>
      <ExpandedCell colSpan={11}>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <ExpandedRowContainer>
            <ExpandedScreenshot
              threatId={threat.threat_id}
              enabled={isExpanded}
              testId="threats-expanded-screenshot"
            />
            <ExpandedSummary
              status={status}
              isLoading={isLoading}
              preCrimeLevel={preCrimeLevel}
              gsbThreatTypes={gsbThreatTypes}
              gsbLoading={gsbLoading}
              dnsARecord={dnsARecord}
              dnsLoading={dnsLoading}
              resurgence={{
                isResurgent: !!threat.resurgent_threat,
                date: threat.identification_status?.updated_at,
              }}
              detailUrl={detailUrl}
            />
            <ExpandedDomainInfo
              ssl={{ value: sslData?.organisation }}
              registrar={{ value: whoisData?.registrar }}
              registered={{ value: whoisData?.registration_date }}
              expires={{ value: whoisData?.expiration_date }}
            />
            <ExpandedDnsRecords
              filteredDnsRecords={filteredDnsRecords}
              dnsLoading={dnsLoading}
            />
          </ExpandedRowContainer>

          <ExpandedSecondRow>
            <ExpandedStatusHistory
              title="Status History"
              statusHistory={sortedStatusHistory}
              isLoading={statusHistoryLoading}
            />
            {threat.identification_status?.value === 'confirmed' && (
              <ExpandedTakedownDetails
                takedownItems={takedownItems}
                takedownLoading={takedownLoading}
                findStatusNameById={findStatusNameById}
              />
            )}
          </ExpandedSecondRow>

          <ExpandedExternalTools
            url={threat.value?.value || threat.threat_name}
          />
        </Collapse>
      </ExpandedCell>
    </TableRow>
  );
};

export default ThreatExpandedRow;
